import { Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { IScreenInput, ScreenInputTypesEnum } from './feedback-data.data';
import { IRadioOption } from 'i2g-ng-ui';
import { IFeedbackResponseItem } from './feedback-data.actions';

@Injectable()
export class FeedbackDataBuilder {
  constructor(private _formBuilder: FormBuilder) {}

  /**
   * Construct angular form control from feedback API 3.0.0 specs
   * https://invoice2go.atlassian.net/wiki/spaces/CORE/pages/717226153/2020-02-05+SA+Reporting+feedback#Stage-2---Dynamic-screen-content---version-3.0.0
   *
   * @param inputs
   */
  buildFormGroup(inputs: IScreenInput[]): FormGroup {
    const formControls = {};

    inputs.forEach((input: IScreenInput) => {
      switch (input.input_type) {
        case ScreenInputTypesEnum.input_single_selection:
          // a radio button with optional text input

          // add the radio button
          formControls[input.input_id] = [null, [Validators.required]];

          // add the optional text input
          const commentField = input.options.find(
            (radioInput) =>
              radioInput.input_type ===
              ScreenInputTypesEnum.radio_button_with_optional_comment
          );
          if (commentField) {
            formControls[commentField.comment.input_id] = [null];
          }

          break;
      }
    });

    return this._formBuilder.group(formControls);
  }

  /**
   * This method builds a data structure that can be used by the template for rendering
   * the actual UI and also stored information for conditional rendering
   *
   * @param inputs
   */
  buildFormFields(inputs: IScreenInput[]): any {
    const formFields = {};

    inputs.forEach((input: IScreenInput) => {
      switch (input.input_type) {
        case ScreenInputTypesEnum.input_single_selection:
          // a radio button with optional text input
          formFields[input.input_id] = {
            type: ScreenInputTypesEnum.radio_button,
            options: this.buildRadioButtonChoices(input.options),
            optional_text: input.options.find(
              (radioInput) =>
                radioInput.input_type ===
                ScreenInputTypesEnum.radio_button_with_optional_comment
            ),
          };

          break;
      }
    });

    return formFields;
  }

  /**
   * Convert form group value to response payload defined by Feedback API
   * https://invoice2go.atlassian.net/wiki/spaces/CORE/pages/717226153/2020-02-05+SA+Reporting+feedback#API-Contracts.1
   *
   * @param formGroup
   */
  buildFormResponse(formGroup: FormGroup): IFeedbackResponseItem[] {
    return Object.keys(formGroup.value)
      .filter((key: string) => formGroup.value[key])
      .map((key: string) => {
        if (this._isTextInput(key)) {
          return {
            item_id: key,
            value: formGroup.value[key],
          };
        }

        return {
          item_id: formGroup.value[key],
          value: true,
        };
      });
  }

  buildRadioButtonChoices(options: IScreenInput[]): IRadioOption[] {
    const radioChoices = [];

    options.forEach((option) => {
      if (option.input_type === ScreenInputTypesEnum.radio_button) {
        radioChoices.push({
          value: option.input_id,
          key: option.label,
        });
      }

      if (
        option.input_type ===
        ScreenInputTypesEnum.radio_button_with_optional_comment
      ) {
        radioChoices.push({
          value: option.radio_button.input_id,
          key: option.radio_button.label,
        });
      }
    });

    return radioChoices;
  }

  private _isTextInput(key: string) {
    return key.endsWith('comment') || key.endsWith('email');
  }
}
